import React, { FC, useEffect, useMemo, useState } from "react";
import { MatchStatus } from "service";
import { getTeamColors } from "components/colors";
import {
  parsePipeStats,
  PipeStatEntry,
  extractSetEntries,
} from "../statistics/helper";

type StatEntry = {
  key: string;
  label: string;
  homeValue: string;
  awayValue: string;
};

type Props = {
  status: MatchStatus;
  homeTeam: string;
  awayTeam: string;
  message?: string;
  description?: string;
};

const DISPLAY_DURATION_MS = 3000;

const normalizePipeEntry = (
  entry: PipeStatEntry,
  index: number
): StatEntry => ({
  key: `pipe-${index}-${entry.label || "stat"}`,
  label: entry.label?.trim?.() || `Stat ${index + 1}`,
  homeValue: entry.home?.trim?.() || "-",
  awayValue: entry.away?.trim?.() || "-",
});

const ensureString = (value: unknown) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number")
    return Number.isFinite(value) ? String(value) : "";
  return String(value);
};

const normalizeObjectEntry = (value: any, index: number): StatEntry | null => {
  if (!value) return null;

  if (Array.isArray(value)) {
    const [label, home, away] = value;
    if (label === undefined && home === undefined && away === undefined)
      return null;
    return {
      key: `arr-${index}`,
      label: ensureString(label) || `Stat ${index + 1}`,
      homeValue: ensureString(home) || "-",
      awayValue: ensureString(away) || "-",
    };
  }

  const label =
    ensureString(
      value.label ??
        value.title ??
        value.name ??
        value.key ??
        value.stat ??
        null
    ) || `Stat ${index + 1}`;

  const home =
    value.homeValue ??
    value.home ??
    value.home_score ??
    value.homeStat ??
    value.h ??
    value.left ??
    (value.values && value.values.home);

  const away =
    value.awayValue ??
    value.away ??
    value.away_score ??
    value.awayStat ??
    value.a ??
    value.right ??
    (value.values && value.values.away);

  if (home === undefined && away === undefined) return null;

  return {
    key: `obj-${index}-${label}`,
    label,
    homeValue: ensureString(home) || "-",
    awayValue: ensureString(away) || "-",
  };
};

const parseDelimitedSegment = (
  segment: string,
  index: number
): StatEntry | null => {
  if (!segment) return null;
  const eqIndex = segment.indexOf("=");
  const label = eqIndex >= 0 ? segment.slice(0, eqIndex).trim() : "";
  const valuesPart = eqIndex >= 0 ? segment.slice(eqIndex + 1).trim() : segment;
  const colonIndex = valuesPart.indexOf(":");
  if (colonIndex < 0) return null;

  const home = valuesPart.slice(0, colonIndex).trim();
  const away = valuesPart.slice(colonIndex + 1).trim();
  if (!home && !away) return null;

  return {
    key: `seg-${index}-${label || "stat"}`,
    label: label || `Stat ${index + 1}`,
    homeValue: home || "-",
    awayValue: away || "-",
  };
};

const parseStatsPayload = (payload: unknown): StatEntry[] => {
  if (!payload) return [];

  if (Array.isArray(payload)) {
    return payload
      .map(normalizeObjectEntry)
      .filter((entry): entry is StatEntry => !!entry);
  }

  if (typeof payload === "string") {
    const trimmed = payload.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      const parsedEntries = parseStatsPayload(parsed);
      if (parsedEntries.length) return parsedEntries;
    } catch {
      /* ignore JSON parse errors */
    }

    const delimiter = trimmed.includes("|")
      ? "|"
      : trimmed.includes(";")
      ? ";"
      : ",";

    return trimmed
      .split(delimiter)
      .map((segment) => segment.trim())
      .filter(Boolean)
      .map(parseDelimitedSegment)
      .filter((entry): entry is StatEntry => !!entry);
  }

  if (typeof payload === "object") {
    const obj = payload as Record<string, unknown>;

    if (Array.isArray(obj.stats)) {
      const statsEntries = parseStatsPayload(obj.stats);
      if (statsEntries.length) return statsEntries;
    }

    if (Array.isArray(obj.entries)) {
      const entryStats = parseStatsPayload(obj.entries);
      if (entryStats.length) return entryStats;
    }

    for (const value of Object.values(obj)) {
      const nested = parseStatsPayload(value);
      if (nested.length) return nested;
    }
  }

  return [];
};

const extractStats = (status: MatchStatus): StatEntry[] => {
  const directTa = parseStatsPayload((status as any)?.ta);
  if (directTa.length) return directTa;

  const courtTa = parseStatsPayload((status as any)?.c?.ta);
  if (courtTa.length) return courtTa;

  const pipeEntries = parsePipeStats((status as any)?.sts);
  if (pipeEntries.length) {
    return pipeEntries.map(normalizePipeEntry);
  }

  return [];
};

const withAlpha = (color: string | undefined, alpha: number) => {
  if (typeof color === "string") {
    const trimmed = color.trim();
    if (/^#([0-9a-f]{6})$/i.test(trimmed)) {
      const alphaHex = Math.round(alpha * 255)
        .toString(16)
        .padStart(2, "0");
      return `${trimmed}${alphaHex}`;
    }
    if (trimmed.startsWith("rgba(")) {
      return trimmed.replace(/rgba\(([^)]+)\)/, (_match, values) => {
        const parts = values.split(",").map((part: string) => part.trim());
        if (parts.length < 3) return trimmed;
        return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
      });
    }
    if (trimmed.startsWith("rgb(")) {
      return trimmed.replace(
        /rgb\(([^)]+)\)/,
        (_match, values) => `rgba(${values}, ${alpha})`
      );
    }
  }
  return `rgba(0, 0, 0, ${alpha})`;
};

export const FullscreenStatsOverlay: FC<Props> = ({
  status,
  homeTeam,
  awayTeam,
  message,
  description,
}) => {
  const stats = useMemo(() => {
    const baseStats = extractStats(status);
    const setEntries = extractSetEntries(((status as any)?.c ?? {}) as any);
    const setStats = setEntries.map((entry, index) => ({
      key: `set-${index}-${entry.key}`,
      label: entry.label,
      homeValue: String(entry.pair[0]),
      awayValue: String(entry.pair[1]),
    }));

    return [...baseStats, ...setStats];
  }, [status]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cycleKey, setCycleKey] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
    setCycleKey((key) => key + 1);
  }, [stats.length]);

  useEffect(() => {
    if (!stats.length) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) =>
        stats.length <= 1 ? 0 : (prev + 1) % stats.length
      );
      setCycleKey((key) => key + 1);
    }, DISPLAY_DURATION_MS);

    return () => clearInterval(interval);
  }, [stats.length]);

  if (!stats.length) return null;

  const activeStat = stats[activeIndex] ?? stats[0];
  const { home: homeColor, away: awayColor } = getTeamColors(status as any);
  const formatValue = (label: string, value: string) => {
    const trimmedLabel = label?.toUpperCase?.().trim() ?? "";
    const needsPercent =
      trimmedLabel === "WIN % 1ST SERVE" ||
      trimmedLabel === "BREAK POINT CONVERSIONS";
    const trimmedValue = value?.trim?.() ?? "";
    if (!needsPercent || !trimmedValue) return trimmedValue || value;
    if (trimmedValue.includes("%")) return trimmedValue;
    const parts = trimmedValue.split(" ").filter(Boolean);
    if (parts.length > 1) {
      parts[0] = `${parts[0]}%`;
      return parts.join(" ");
    }
    return `${trimmedValue}%`;
  };

  const homeValue = formatValue(activeStat.label, activeStat.homeValue);
  const awayValue = formatValue(activeStat.label, activeStat.awayValue);

  return (
    <div
      data-testid="tennis-fullscreen-stats"
      className="pointer-events-none absolute inset-0 z-[4000] text-white"
      style={{ backdropFilter: "blur(2px)" }}
    >
      {(message || description) && (
        <div
          className="animate-tennis-heading absolute left-0 right-0 z-30 mx-auto flex flex-col items-center justify-center text-center"
          style={{ top: "clamp(12px, 1.2vw, 24px)" }}
        >
          {message && (
            <div
              className="font-extrabold uppercase"
              style={{
                fontSize: "clamp(1.4rem, 2.2vw, 3.2rem)",
                letterSpacing: "0.08em",
                textShadow: "0 0.2vw 0.5vw rgba(0,0,0,0.45)",
              }}
            >
              {message}
            </div>
          )}
          {description && (
            <div
              className="font-semibold"
              style={{
                fontSize: "clamp(0.95rem, 1.3vw, 1.8rem)",
                textShadow: "0 0.15vw 0.4vw rgba(0,0,0,0.4)",
                opacity: 0.9,
              }}
            >
              {description}
            </div>
          )}
        </div>
      )}

      <div className="absolute inset-0 grid grid-cols-2 overflow-hidden shadow-[0_2vw_5vw_rgba(0,0,0,0.45)]">
        <div className="relative flex flex-col items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0"
            style={{ background: withAlpha(homeColor, 0.85) }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1), transparent 65%)",
            }}
          />
          <div
            key={`home-${cycleKey}`}
            className="relative z-10 font-extrabold text-center"
            style={{
              fontSize: "clamp(2rem, 6vw, 8rem)",
              letterSpacing: "0.02em",
              animation: `tennis-cycle ${DISPLAY_DURATION_MS}ms ease-in-out forwards`,
            }}
          >
            {homeValue || activeStat.homeValue}
          </div>
          <div
            className="relative z-10"
            style={{
              width: "min(60%, 220px)",
              height: "clamp(2px, 0.35vw, 6px)",
              margin: "clamp(12px, 2.5vw, 24px) 0 clamp(14px, 3vw, 32px)",
              background: "rgba(255,255,255,0.65)",
            }}
          />
          <div
            className="relative z-10 text-center font-semibold"
            style={{
              fontSize: "clamp(1rem, 1.8vw, 2.6rem)",
              textShadow: "0 0.1vw 0.4vw rgba(0,0,0,0.45)",
              lineHeight: 1.2,
            }}
          >
            {homeTeam || "Home"}
          </div>
        </div>

        <div className="relative flex flex-col items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0"
            style={{ background: withAlpha(awayColor, 0.85) }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1), transparent 65%)",
            }}
          />
          <div
            key={`away-${cycleKey}`}
            className="relative z-10 font-extrabold text-center"
            style={{
              fontSize: "clamp(2rem, 6vw, 8rem)",
              letterSpacing: "0.02em",
              animation: `tennis-cycle ${DISPLAY_DURATION_MS}ms ease-in-out forwards`,
            }}
          >
            {awayValue || activeStat.awayValue}
          </div>
          <div
            className="relative z-10"
            style={{
              width: "min(60%, 220px)",
              height: "clamp(2px, 0.35vw, 6px)",
              margin: "clamp(12px, 2.5vw, 24px) 0 clamp(14px, 3vw, 32px)",
              background: "rgba(255,255,255,0.65)",
            }}
          />
          <div
            className="relative z-10 text-center font-semibold"
            style={{
              fontSize: "clamp(1rem, 1.8vw, 2.6rem)",
              textShadow: "0 0.1vw 0.4vw rgba(0,0,0,0.45)",
              lineHeight: 1.2,
            }}
          >
            {awayTeam || "Away"}
          </div>
        </div>
      </div>

      <div
        key={`label-${cycleKey}`}
        className="absolute left-0 right-0 z-30 mx-auto flex items-center justify-center text-center font-bold uppercase"
        style={{
          bottom: "clamp(12px, 1.2vw, 24px)",
          fontSize: "clamp(1rem, 2vw, 2.6rem)",
          letterSpacing: "0.07em",
          padding: "clamp(10px, 1.4vw, 18px) clamp(12px, 1.8vw, 22px)",
          animation: `tennis-cycle ${DISPLAY_DURATION_MS}ms ease-in-out forwards`,
          textShadow: "0 0.2vw 0.4vw rgba(0,0,0,0.4)",
        }}
      >
        {activeStat.label}
      </div>
    </div>
  );
};
